/**
 * Contrast guard rail (concept 9.1, 9.6.5).
 *
 * A tenant picks its own colours; accessibility is an acceptance criterion of
 * every theme. So the combination has to be checkable before it is stored;
 * this is the shared implementation both the backoffice preview and the
 * server-side validation use.
 *
 * WCAG 2.2 relative luminance and contrast ratio.
 */
const CONTRAST_THRESHOLDS = {
    AA: { normal: 4.5, large: 3 },
    AAA: { normal: 7, large: 4.5 },
};
/** Accepts `#rgb`, `#rrggbb` (with or without leading `#`). */
export function parseHexColor(value) {
    const hex = value.trim().replace(/^#/, '');
    if (/^[0-9a-f]{3}$/i.test(hex)) {
        const r = hex[0];
        const g = hex[1];
        const b = hex[2];
        return {
            r: Number.parseInt(`${r}${r}`, 16),
            g: Number.parseInt(`${g}${g}`, 16),
            b: Number.parseInt(`${b}${b}`, 16),
        };
    }
    if (/^[0-9a-f]{6}$/i.test(hex)) {
        return {
            r: Number.parseInt(hex.slice(0, 2), 16),
            g: Number.parseInt(hex.slice(2, 4), 16),
            b: Number.parseInt(hex.slice(4, 6), 16),
        };
    }
    return null;
}
export function relativeLuminance(color) {
    const channel = (value) => {
        const srgb = value / 255;
        return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}
/**
 * Contrast ratio between two hex colours, 1 … 21.
 * Returns `null` when a value cannot be parsed; the caller decides whether an
 * unparseable colour blocks or only warns.
 */
export function contrastRatio(foreground, background) {
    const fg = parseHexColor(foreground);
    const bg = parseHexColor(background);
    if (fg === null || bg === null) {
        return null;
    }
    const lighter = Math.max(relativeLuminance(fg), relativeLuminance(bg));
    const darker = Math.min(relativeLuminance(fg), relativeLuminance(bg));
    return (lighter + 0.05) / (darker + 0.05);
}
export function meetsContrast(foreground, background, level = 'AA', size = 'normal') {
    const ratio = contrastRatio(foreground, background);
    return ratio !== null && ratio >= CONTRAST_THRESHOLDS[level][size];
}
/**
 * Checks a list of foreground/background pairs at once: the shape the tenant
 * theme editor needs in order to warn or block (concept 9.1).
 */
export function auditContrastPairs(pairs, level = 'AA') {
    return pairs.map((entry) => {
        const size = entry.size ?? 'normal';
        const required = CONTRAST_THRESHOLDS[level][size];
        const ratio = contrastRatio(entry.foreground, entry.background);
        return {
            pair: entry.pair,
            foreground: entry.foreground,
            background: entry.background,
            ratio,
            required,
            passes: ratio !== null && ratio >= required,
        };
    });
}
//# sourceMappingURL=contrast.js.map